/**
 * @author        tae@zumper.com (Tae Yoon)
 * @copyright     Copyright (c) 2018, Zumper
 * @description   ExternalMessageButton Component
 */

import { Button, type ButtonProps } from '@zcode-ui/controls'
import { cn } from '@zcode-ui/shadz-ui'
import { trackGAEvent } from '@zcode/nr-utils'
import { sourceNameByFeed } from '@zcode/nr/es/shared/constants/source'
import { stopPropagation } from '@zcode/nr/es/shared/utils/utils'
import type { Listable, Property } from '@zcode/state-type'
import { rootRelative } from '@zcode/utils'
import React, { type ForwardedRef, type MouseEvent, forwardRef } from 'react'
import { default as ExternalIcon } from '../svg/icons/ExternalIcon'

interface Props extends Pick<ButtonProps<'a'>, 'className' | 'variant'> {
  analyticChannel: 'Message'
  feedName: Listable['feedName'] | Property['feedName']
  iconClassName?: string
  iconColor?: string
  isFullWidth?: boolean
  isShortTermFeed: boolean | undefined
  onCtaClick?: () => void
  text?: string
  textClassName?: string
  url: string
}

export const ExternalMessageButton = forwardRef(
  (
    {
      analyticChannel,
      className,
      feedName,
      isFullWidth,
      onCtaClick,
      iconClassName,
      iconColor,
      isShortTermFeed,
      text = 'Check availability',
      textClassName,
      url,
      variant,
    }: Props,
    ref: ForwardedRef<HTMLAnchorElement & HTMLButtonElement>
  ) => {
    const handleClick = (event: MouseEvent<HTMLElement>) => {
      if (event) {
        stopPropagation(event)
      }
      onCtaClick?.()
      trackGAEvent(analyticChannel, 'External', feedName)
    }

    return (
      <Button
        as="a"
        className={cn(isFullWidth && 'w-full', className)}
        href={rootRelative(url)}
        onClick={handleClick}
        ref={ref}
        rel="nofollow noopener noreferrer"
        target="_blank"
        variant={feedName ? 'pink' : variant}
      >
        <div className="flex items-center justify-center w-full">
          <ExternalIcon className={iconClassName} color={iconColor} />
          <div className={textClassName || 'pl-[10px] text-gray-0 hover:text-gray-0'}>
            {feedName
              ? // @ts-expect-error feedName might not be in sourceNameByFeed
                `Book on ${sourceNameByFeed[feedName] ?? feedName}`
              : isShortTermFeed
                ? 'Book Now'
                : text}
          </div>
        </div>
      </Button>
    )
  }
)
